import React from 'react'
import { styled } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Grid from '@mui/material/Grid'
import Button from '@mui/material/Button'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import ShowChartIcon from '@mui/icons-material/ShowChart'
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn'
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt'
import { useNavigate } from 'react-router-dom'

// Styled Components
const Section = styled('section')({
  backgroundColor: '#0E1116',
  padding: '10px 0',
  color: '#fff',
})

const Tag = styled(Box)({
  backgroundColor: '#112728',
  color: '#1DAFA1',
  padding: '8px 16px',
  borderRadius: '20px',
  fontSize: '0.9rem',
  fontWeight: 600,
  display: 'inline-block',
  marginBottom: '24px',
})

const Title = styled(Typography)(({ theme }) => ({
  fontSize: '3rem',
  fontWeight: 700,
  textAlign: 'center',
  marginBottom: '12px',
  margin: '0 10px',
  '& span': {
    color: '#1DAFA1',
  },
  [theme.breakpoints.down('md')]: {
    fontSize: '2.5rem',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '2rem',
  },
}))

const Subtitle = styled(Typography)(({ theme }) => ({
  color: '#9AA6B2',
  fontSize: '1rem',
  maxWidth: '600px',
  margin: '10px auto',
  marginBottom: '50px',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.9rem',
    marginBottom: '30px',
    padding: '0 16px',
  },
}))

const ProductCard = styled(Box)(({ hoverBg }) => ({
  backgroundColor: '#13161D',
  borderRadius: '24px',
  padding: '30px',
  height: '100%',
  border: '1px solid transparent',
  transition: 'all 0.3s ease',
  display: 'flex',
  flexDirection: 'column',
  '&:hover': {
    backgroundColor: hoverBg || '#13161D',
    border: '1px solid #1DAFA1',
    transform: 'translateY(-10px)',
    boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
    '& .icon-box': {
      backgroundColor: '#1DAFA1',
      color: '#fff',
    },
    '& h5': {
      color: '#1DAFA1',
    },
    '& .action-button': {
      backgroundColor: '#1DAFA1',
      color: 'white',
      border: '1px solid #1DAFA1',
    },
  },
}))

const CardTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  marginBottom: '8px',
  fontSize: '1.5rem',
  transition: 'color 0.3s ease',
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.25rem',
  },
}))

const CardDescription = styled(Typography)(({ theme }) => ({
  color: '#9AA6B2',
  lineHeight: 1.6,
  fontSize: '1rem',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.875rem',
  },
}))

const IconBox = styled(Box)({
  width: '64px',
  height: '64px',
  borderRadius: '16px',
  backgroundColor: 'rgba(27, 76, 74, 0.4)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: '20px',
  color: '#1DAFA1',
  transition: 'all 0.3s ease',
  '& svg': {
    fontSize: '32px',
  },
})

const FeatureList = styled('ul')(({ theme }) => ({
  listStyle: 'none',
  padding: 0,
  margin: '10px 0 10px 0',
  flex: 1,
  '& li': {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '4px',
    color: '#B1B5C3',
    fontSize: '0.95rem',
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.875rem',
    },
    '&::before': {
      content: '""',
      width: '6px',
      height: '6px',
      backgroundColor: '#1DAFA1',
      borderRadius: '50%',
      marginRight: '12px',
    },
  },
}))

const ActionButton = styled(Button)({
  width: '100%',
  padding: '8px',
  borderRadius: '12px',
  textTransform: 'none',
  fontSize: '1rem',
  fontWeight: 600,
  color: '#fff',
  border: '1px solid #23262F',
  backgroundColor: 'transparent',
  transition: 'all 0.3s ease',
})

const products = [
  {
    icon: <TrendingUpIcon />,
    title: 'Spot Trading',
    description:
      'Buy and sell cryptocurrencies instantly with deep liquidity and competitive fees.',
    features: ['500+ Trading Pairs', 'Real-time Charts', 'Advanced Orders'],
    action: 'Start Trading',
    hoverBg: '#1E2329', // Dark Grey
    path: '/spot-usdt/BTC-USDT',
  },
  {
    icon: <ShowChartIcon />,
    title: 'Futures',
    description:
      'Trade perpetual contracts with up to 125x leverage on major cryptocurrencies.',
    features: ['USDT-M Perpetual', 'Up to 125x Leverage', 'Risk Management'],
    action: 'Trade Futures',
    hoverBg: '#1E2329',
    path: '/futures/trade/BTCUSDT',
  },
  {
    icon: <MonetizationOnIcon />,
    title: 'Earn',
    description:
      'Grow your crypto holdings with flexible staking, savings, and yield products.',
    features: ['Flexible Staking', 'High APY Returns', 'Auto-compound'],
    action: 'Start Earning',
    hoverBg: '#1E2329',
    path: '/earn',
  },
  {
    icon: <SignalCellularAltIcon />,
    title: 'TradeIQ',
    description:
      'Get expert trading signals and market insights from professional analysts.',
    features: ['Expert Analysis', 'Real-time Alerts', 'Performance Tracking'],
    action: 'Get Signals',
    hoverBg: '#1E2329',
    path: '/signal',
  },
]

const BackgroundCircle = styled(Box)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '60%',
  height: '60%',
  borderRadius: '50%',
  background:
    'radial-gradient(circle, rgba(29, 176, 161, 0.08) 0%, rgba(14, 17, 22, 0) 70%)',
  pointerEvents: 'none',
  zIndex: 0,
})

function ExploreOurProducts() {
  return (
    <Section sx={{ position: 'relative', overflow: 'hidden' }}>
      <BackgroundCircle />
      <div className='container' style={{ position: 'relative', zIndex: 1 }}>
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Tag>Our Products</Tag>
          <Title variant='h1'>
            Explore Our <span style={{ color: '#1DAFA1' }}>Products</span>
          </Title>
          <Subtitle>
            Everything you need to trade, invest, and grow your crypto portfolio
            in one platform.
          </Subtitle>
        </Box>

        <Grid container spacing={4}>
          {products.map((product, index) => (
            <Grid item xs={12} md={6} lg={3} key={index}>
              <ProductCard hoverBg={product.hoverBg}>
                <IconBox className='icon-box'>{product.icon}</IconBox>
                <CardTitle variant='h5'>{product.title}</CardTitle>
                <CardDescription variant='body1'>
                  {product.description}
                </CardDescription>
                <FeatureList>
                  {product.features.map((feature, i) => (
                    <li key={i}>{feature}</li>
                  ))}
                </FeatureList>
                <ActionButton
                  className='action-button'
                  variant='outlined'
                  component='a'
                  href={`${window.location.origin}${product?.path}`}
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  {product.action}
                </ActionButton>
              </ProductCard>
            </Grid>
          ))}
        </Grid>
      </div>
    </Section>
  )
}

export default ExploreOurProducts
